import React from 'react'
import { HeaderGroup } from 'react-table'
import cx from 'clsx'

import SortableHeader from '../SortableHeader'
import FilterableColumnHeader, { FilterOption, DateFilterValue } from '../FilterableColumnHeader'

import { SortOrder } from '@type/common'

export interface ColumnFilterConfig {
  filterKey: string
  filterOptions: FilterOption[]
  selectedFilters: string[]
  onFilterChange: (values: string[]) => void
  isDateColumn?: boolean
  dateValue?: DateFilterValue
  onDateChange?: (value: DateFilterValue | null) => void
}

export interface TableHeaderProps<TRecord extends object> {
  headerGroup: HeaderGroup<TRecord>
  className?: string
  thClassName?: string
  thClassNames?: { [key: string]: string }
  isDnd?: boolean
  columnFilterConfigs?: Record<string, ColumnFilterConfig>
  sortState?: Record<string, { order: SortOrder; index: number }>
}

export const TableHeader = <TRecord extends object>({
  headerGroup,
  className,
  thClassName,
  thClassNames,
  isDnd = false,
  columnFilterConfigs = {},
  sortState = {},
}: TableHeaderProps<TRecord>) => {
  const { key, ...props } = headerGroup.getHeaderGroupProps()
  const dndCmp = isDnd && <th className={cx(thClassName)} />

  return (
    <tr key={key} {...props} className={cx(className)}>
      {dndCmp}
      {headerGroup.headers.map((column) => {
        const { key, ...props } = column.getHeaderProps({
          style: { maxWidth: column.maxWidth },
        })
        const sortProps = column.getSortByToggleProps()
        const filterConfig = columnFilterConfigs[column.id]
        const sortInfo = sortState[column.id]
        const currentOrder = sortInfo?.order ?? (column.isSorted ? (column.isSortedDesc ? SortOrder.desc : SortOrder.asc) : undefined)

        let header: React.ReactNode

        if (!column.defaultCanSort) {
          // Non-sortable column
          header = column.render('Header')
        } else if (filterConfig) {
          // Sortable and filterable column - use new FilterableColumnHeader
          header = (
            <FilterableColumnHeader
              order={currentOrder}
              onSortClick={sortProps.onClick}
              filterOptions={filterConfig.filterOptions}
              selectedFilters={filterConfig.selectedFilters}
              onFilterChange={filterConfig.onFilterChange}
              isDateColumn={filterConfig.isDateColumn}
              dateValue={filterConfig.dateValue}
              onDateChange={filterConfig.onDateChange}
              className={(column as unknown as { className: string }).className}
            >
              {column.render('Header')}
            </FilterableColumnHeader>
          )
        } else {
          // Sortable but not filterable - use original SortableHeader
          header = (
            <SortableHeader
              onClick={sortProps.onClick}
              order={currentOrder}
              className={(column as unknown as { className: string }).className}
            >
              {column.render('Header')}
            </SortableHeader>
          )
        }

        return (
          <th key={key} {...props} className={thClassName}>
            <div className={thClassNames && thClassNames[column.id]}>{header}</div>
          </th>
        )
      })}
    </tr>
  )
}

export default React.memo(TableHeader)
